/**
 * b19-ui-dex-completion: Dex completion percentages for the Pokedex menu.
 *
 * Returns two equal-weight completion figures for the currently selected Dex
 * filter:
 *   - "In GO": caught / species currently available in Pokemon GO for the
 *     selected Dex (Shiny uses the shiny-released roster, every other filter
 *     uses the full available-in-GO roster).
 *   - "National": caught / current National Dex total. The National
 *     denominator is filter independent on purpose: the user wants the
 *     National number to represent the full Pokedex regardless of the active
 *     filter.
 *
 * dex-availability-layer: the "In GO" denominator now comes from the SAME
 * client-side availability layer the grid uses to decide which cells are
 * released vs unreleased/locked (`availableRosterForVariant` in
 * variantAvailability.ts). That layer is the event-ingested source of truth —
 * it folds the bundled PoGoAPI roster together with the curated corrections and
 * the LeekDuck event releases — so the ring denominator and the number of
 * available grid cells can never drift. It replaces the earlier reliance on the
 * standalone GET /api/dex-availability count, which did not see those
 * corrections and so left the ring divided by a stale pre-layer total.
 *
 * The National total still comes from GET /api/dex-availability via
 * useDexAvailability (the National Dex size is a server-reported figure that
 * grows a generation at a time). While that query is pending or if it fails the
 * hook uses the in-file National placeholder and source 'placeholder'; once a
 * real response is read it uses the live National total and source 'remote'.
 *
 * The caught count is read from the SAME source the Pokedex menu uses for its
 * caught headline (composedVariantSummary over the local dex-state store), so
 * the percentages can never disagree with the "caught" number on the page. The
 * hook subscribes to DEX_STATE_EVENT via useSyncExternalStore so both cells
 * update reactively on every manual toggle, scan write, or filter change.
 */
import { useSyncExternalStore } from "react";
import {
  DEX_TOTALS_EVENT,
  composedVariantSummarySnapshot,
  composedVariantPresentIdsSnapshot,
} from "@/lib/dexStateStore";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import { curatedRosterFor } from "@/lib/pogoAvailability";
import { formDexCells, isFormDexVariant } from "@/lib/formDexRoster";
import {
  availableRosterForVariant,
  isKnownUntradableSpecies,
  isTradeGatedVariant,
  untradableCountWithin,
} from "@/lib/variantAvailability";
import { MEGA_EXTRA_FORMS, GMAX_EXTRA_FORMS } from "@/lib/megaGmaxSprites";
import { useDexAvailability } from "./useDexAvailability";

export type DexFilter =
  | "all"
  | "shiny"
  | "lucky"
  | "xxl"
  | "xxs"
  | "perfect"
  | string;

export interface DexCompletion {
  inGoCaught: number;
  inGoTotal: number;
  inGoPct: number; // 0..100, rounded to 1 decimal
  nationalCaught: number;
  nationalTotal: number;
  nationalPct: number; // 0..100, rounded to 1 decimal
  source: "placeholder" | "remote";
}

// The National denominator comes from GET /api/dex-availability via
// useDexAvailability. This constant is the on-error / pending fallback so a
// percentage is never blank or broken; the endpoint itself serves the same
// baseline when its cache is empty, so the live and fallback numbers agree until
// the National total moves. (The In-GO denominator no longer needs a placeholder
// — it is read synchronously from the bundled availability layer.)
const NATIONAL_DEX_TOTAL = 1025;

/** Stable empty set for the useSyncExternalStore SSR fallback (no per-render alloc). */
const EMPTY_ID_SET: ReadonlySet<number> = new Set<number>();

/**
 * Map the Pokedex page filter token to the PokedexVariant the dex-state store
 * keys catches under. The page's main "All" tab is the "pokemon" variant.
 */
function filterToVariant(filter: DexFilter): PokedexVariant {
  switch (filter) {
    case "all":
      return "pokemon";
    case "shiny":
      return "shiny";
    case "lucky":
      return "lucky";
    case "xxl":
      return "xxl";
    case "xxs":
      return "xxs";
    case "perfect":
      return "perfect";
    default:
      // Any other filter token (mega, gmax, shadow, purified, shundo, or a
      // value already in PokedexVariant form) is passed through unchanged.
      return filter as PokedexVariant;
  }
}

/**
 * b19-fix-curated-totals: Mega / G-Max / Shadow / Purified are NOT 1025-wide.
 * Only a fixed, curated set of species can ever carry those variants in
 * Pokemon GO (e.g. ~few dozen Megas), so a `/1025` or `/937` denominator is
 * meaningless on those tabs and pins the rings at ~0%. When the active variant
 * has a curated GO-availability roster, BOTH the In-GO and National
 * denominators become that roster's size — the only honest "out of" for a
 * variant that does not span the whole National Dex. curatedRosterFor is the
 * single source of truth shared with the grid (dexGridFilter.baseDexNumbers),
 * so the ring denominator and the number of grid cells stay in lockstep.
 * Returns null for whole-roster variants (pokemon/shiny/lucky/perfect/xxs/xxl)
 * and for manual-only customs (shundo/shinyLucky), which keep their existing
 * national / in-GO denominators.
 */
function curatedRosterTotal(variant: PokedexVariant): number | null {
  const roster = curatedRosterFor(variant);
  if (!roster) return null;
  // pokedex-mega-xy: curated rosters are keyed by base id, but a few species
  // render a second form cell that also counts toward the dex — Mega Charizard/
  // Mewtwo/Raichu (Y) and G-Max Urshifu (Rapid). Add those extra cells so the
  // ring denominator matches the grid's cell count.
  if (variant === "mega") return roster.length + MEGA_EXTRA_FORMS.length;
  if (variant === "gmax") return roster.length + GMAX_EXTRA_FORMS.length;
  // form-dexes: the roster is the SPECIES that have a form, but the grid draws
  // one cell per FORM (Meowth is two, Pikachu is a wardrobe). The cell count is
  // the only denominator the ring and the grid can agree on.
  if (isFormDexVariant(variant)) return formDexCells(variant).length;
  return roster.length;
}

/** Percentage of caught over total, clamped to [0, 100] and rounded to 1 decimal. */
function pct(caught: number, total: number): number {
  if (total <= 0) return 0;
  const raw = (caught / total) * 100;
  const clamped = Math.min(100, Math.max(0, raw));
  return Math.round(clamped * 10) / 10;
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  // Perf (dex-performance-lag): the completion rings track the TOTALS snapshot,
  // which only refreshes at lifecycle checkpoints (app open, manual-edit toggle
  // off, live-scan broadcast close, screenshot finish) — not on every store
  // mutation. So a burst of scan writes no longer re-scans the store per frame.
  window.addEventListener(DEX_TOTALS_EVENT, onChange);
  return () => window.removeEventListener(DEX_TOTALS_EVENT, onChange);
}

export function useDexCompletion(filter: DexFilter): DexCompletion {
  const variant = filterToVariant(filter);
  // useSyncExternalStore re-reads the caught count when the totals snapshot is
  // committed. composedVariantSummarySnapshot returns the cached snapshot between
  // checkpoints, so this getSnapshot stays O(1) on unrelated re-renders. This is
  // the TOTAL caught across the (cascaded) variant — the National-ring numerator
  // and the page headline both use it.
  const caught = useSyncExternalStore(
    subscribe,
    () => composedVariantSummarySnapshot(variant).present,
    () => 0,
  );
  // The set of species actually caught for this variant, for the In-GO ring's
  // present ∩ released intersection below. Same checkpoint cadence as `caught`.
  const presentIds = useSyncExternalStore(
    subscribe,
    () => composedVariantPresentIdsSnapshot(variant),
    () => EMPTY_ID_SET,
  );

  // The National total is global (not filter-scoped); the availability query is
  // cached for 5 minutes and does not refetch on filter change. Until it
  // resolves (or if it fails) we use the in-file National fallback, so the user
  // never sees a blank or broken percentage. (The In-GO total below is read
  // synchronously from the bundled availability layer and needs no fetch.)
  const { data: availability, isResolved } = useDexAvailability();

  // National denominator: curated (sparse) variants collapse to their eligible
  // roster size (a /1025 is meaningless for a few-dozen-species Mega dex);
  // whole-dex variants divide by the National Dex total, less the species the
  // game refuses to trade on the trade-only dexes (which can never be Lucky).
  //
  // The offset used to be "every Mythical", a proxy for tradability that was
  // wrong in both directions: it stripped the tradable Mythicals (Meltan #808,
  // Melmetal #809, Pecharunt #1025) while keeping the untradable non-Mythicals
  // (Zygarde #718, Eternatus #890). The MEMBERSHIP now matches the GO-Released
  // ring's roster (released ∩ tradable), so a Meltan Lucky catch no longer
  // counts against an "out of" its slot was removed from. Shiny-Lucky is
  // trade-gated too and was getting no offset at all; it now shares the rule.
  //
  // The offset is 22 today (23 Mythicals). It was briefly 23 as well, because
  // Basculegion #902 was being counted as untradable off the back of having no
  // Game Master row at all — an unreleased species the game has said nothing
  // about, not one it refuses to trade. See variantAvailability's coverage set.
  const rosterTotal = curatedRosterTotal(variant);
  const nationalDexTotal = availability?.nationalDexTotal ?? NATIONAL_DEX_TOTAL;
  const untradableOffset = isTradeGatedVariant(variant)
    ? untradableCountWithin(nationalDexTotal)
    : 0;
  const nationalTotal = rosterTotal ?? nationalDexTotal - untradableOffset;

  // In-GO denominator: the size of THIS variant's availability roster — the same
  // client-side layer the grid reads (variantAvailability.availableRosterForVariant),
  // so the ring's "out of" always equals the count of obtainable cells the grid
  // shows. The layer already bakes in the per-variant gating (Shiny → shiny
  // roster, Lucky → released ∩ tradable, Mega/G-Max/Shadow/Purified → curated
  // roster), so no separate Mythical/tradable offsets are applied here. Mega also
  // renders Charizard-Y and Mewtwo-Y as extra cells beyond the base-id roster, so
  // those are added to keep the ring in lockstep with the grid. Fails open to the
  // National total if a roster is ever unknown (never divide by a wrong count).
  const availableRoster = availableRosterForVariant(variant);
  // A secondary form cell counts only while its species is released: G-Max
  // Urshifu Rapid is a grid cell today but not an obtainable one.
  const extraFormCells =
    variant === "mega"
      ? MEGA_EXTRA_FORMS.length
      : variant === "gmax"
        ? GMAX_EXTRA_FORMS.filter((f) => availableRoster?.has(f.baseId) ?? true).length
        : 0;
  // form-dexes count CELLS on both rings; the In-GO one drops the cells GO has
  // not released yet (a costume bundled ahead of its event weekend), which is
  // the same released/unreleased split every other dex's rings make.
  const inGoTotal = isFormDexVariant(variant)
    ? formDexCells(variant).filter((c) => c.released).length
    : availableRoster
      ? availableRoster.size + extraFormCells
      : nationalTotal;

  // In-GO numerator: for the WHOLE-DEX rings (Pokémon / Shiny / Lucky / IV
  // filters) this is the ACCURATE "caught among the released roster" count, i.e.
  // present ∩ availableRoster — NOT the raw total caught. The device that read a
  // nonsensical "956/951" (>100%) had caught species the released layer doesn't
  // list (roster gaps + cascade from curated variants whose base isn't released);
  // those are genuinely caught but are not part of THIS ring's denominator, so
  // they must not inflate the numerator either. Intersecting yields e.g. 947/951
  // — the honest "you're missing 4 released species" the trainer expects.
  //
  // Curated variants (mega/gmax/shadow/purified) are LEFT on the raw caught
  // count: their roster is keyed by base id but the grid/denominator add extra
  // form cells (Charizard-Y, Mewtwo-Y) that share a base dexNumber, so a
  // by-dexNumber intersection would under-count them and the ring could never
  // reach 100%. `curatedRosterFor(variant) == null` is exactly the whole-dex
  // predicate (it returns a roster only for the curated variants). Fails open to
  // the raw caught count if a roster is unknown, never dividing by a wrong count.
  const isWholeDex = curatedRosterFor(variant) == null;
  const inGoCaught =
    isWholeDex && availableRoster
      ? countInRoster(presentIds, availableRoster)
      : caught;

  // National numerator: every caught species, MINUS the ones the denominator has
  // no slot for. A catch outside the GO-released roster is still a National-dex
  // catch (our release data is incomplete, so that catch may well be real) — but
  // an untradable species on a trade-only dex is impossible, not merely
  // unreleased, and the offset above already removed its slot. Counting it
  // anyway is how a hand-tapped Lucky Keldeo #647 read 921/1,002 against a
  // GO-Released ring of 920/933. Subtracted from `caught` rather than recounting
  // presentIds so the extra-form accounting in the summary is preserved.
  const untradableCaught = isTradeGatedVariant(variant)
    ? countUntradable(presentIds)
    : 0;
  const nationalCaught = Math.max(0, caught - untradableCaught);

  return {
    inGoCaught,
    inGoTotal,
    inGoPct: pct(inGoCaught, inGoTotal),
    nationalCaught,
    nationalTotal,
    nationalPct: pct(nationalCaught, nationalTotal),
    // 'remote' once the client has read a real response (whether the endpoint
    // served pogoapi or fallback data); 'placeholder' while pending or failed.
    source: isResolved ? "remote" : "placeholder",
  };
}

/** Count how many caught ids Pokémon GO refuses to trade (impossible on a Lucky dex). */
function countUntradable(present: ReadonlySet<number>): number {
  let n = 0;
  for (const id of present) if (isKnownUntradableSpecies(id)) n += 1;
  return n;
}

/** Count how many ids in `present` fall inside `roster` (present ∩ roster size). */
function countInRoster(
  present: ReadonlySet<number>,
  roster: ReadonlySet<number>,
): number {
  // Iterate the smaller set for speed; membership check is symmetric.
  const [small, big] =
    present.size <= roster.size ? [present, roster] : [roster, present];
  let n = 0;
  for (const id of small) if (big.has(id)) n += 1;
  return n;
}
